// Library Methods
import { useTranslation } from "react-i18next";
import React, { useState, useMemo } from "react";

// Components
import { Divider, Grid, TextField, Typography } from "@mui/material";
import ExamCardButton from "../ui/ExamCardButton";
import ExamCardToggles from "../ui/ExamCardToggles";
import WhichEyeSection from "../ui/WhichEyeSection";
import TitleWithTooltip from "../../../../../components/UI/TitleWithTooltip";
import ExamCardContainer from "../ui/ExamCardContainer";
import ExamCardHeader from "../ui/ExamCardHeader";

// Utilities
import {
  FixationLossMethodOptions,
  FixationPointShapeOptions,
} from "../../../../../utils/examReportsHelper";
import {
  CustomTextFieldSolid,
  CustomTextFieldOutlinedInputProps,
} from "../../../../../styles/muiStylesHelper";
import {
  DEFAULT_DECIBEL_VALUE_SCREENING,
  gridLabels,
  GridTypes,
  MAX_DECIBEL_VALUE,
  MIN_DECIBEL_VALUE,
  StimulusColors,
  StimulusTypes,
} from "../../../../../enums";

const VisualFieldScreeningCard = ({
  label,
  examAlgorithm,
  selectedExamCards,
  updateExamCardProperty,
  onChangeEye,
  onChangeSectionOrder,
  onToggleCard,
}) => {
  // internationalization
  const { t } = useTranslation();

  const defaultVFScreening = {
    examFieldType: examAlgorithm,
    examType: "SCREEN_FAST",
    decibelValue: DEFAULT_DECIBEL_VALUE_SCREENING,
    eyeCheckboxes: { right: true, left: true },
    visualGrid: GridTypes.C40,
    stimulusSizeType: StimulusTypes.III,
    stimulusColor: StimulusColors.WHITE,
    fixationType: {
      fixationPointShapeType: FixationPointShapeOptions.Sphere,
      fixationLossMethod: FixationLossMethodOptions.EyeTracking,
    },
    sectionOrder: { right: 0, left: 1 },
    optionalToggles: {
      autoPause: true,
      calibration: true,
      encouragement: true,
      requiresTutorial: true,
    },
  };

  const selectedExamCard = selectedExamCards.find(
    (examCard) => examCard.examFieldType === examAlgorithm
  );
  const examCardInfo = selectedExamCard ?? defaultVFScreening;
  const isActive = Boolean(selectedExamCard);

  const {
    decibelValue: initialDecibelValue,
    visualGrid,
    eyeCheckboxes,
    sectionOrder,
    optionalToggles,
    fixationType,
    stimulusSizeType,
    stimulusColor,
  } = examCardInfo;

  const [decibelValue, setDecibelValue] = useState(initialDecibelValue);

  const vfScreenGrids = useMemo(
    () => [
      { type: GridTypes.C40, label: gridLabels[GridTypes.C40] },
      { type: GridTypes.G24, label: gridLabels[GridTypes.G24] },
      { type: GridTypes.G24C, label: gridLabels[GridTypes.G24C] },
      { type: GridTypes.G30, label: gridLabels[GridTypes.G30] },
      { type: GridTypes.G10, label: gridLabels[GridTypes.G10] },
    ],
    []
  );

  const vfScreenStimulusSizes = useMemo(
    () => [
      { type: StimulusTypes.III, label: StimulusTypes.III },
      { type: StimulusTypes.IV, label: StimulusTypes.IV },
      { type: StimulusTypes.V, label: StimulusTypes.V },
    ],
    []
  );

  const vfScreenStimulusColors = useMemo(
    () => [
      { type: StimulusColors.WHITE, label: t("White") },
      { type: StimulusColors.RED, label: t("Red") },
    ],
    [t]
  );

  const textStyle = {
    color: isActive ? "#FFF" : "#5B6D7A",
    fontSize: "14px",
  };

  return (
    <ExamCardContainer isActive={isActive}>
      <ExamCardHeader
        label={label}
        isActive={isActive}
        onChange={(e) => {
          onToggleCard(e, defaultVFScreening);
        }}
      />
      <Grid container display="flex" flexDirection="column" gap="8px">
        {/* visual field method */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_method")}
            tooltip={t("method_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />
          <ExamCardButton
            isSelected
            updateValue={() =>
              updateExamCardProperty("examType", "SCREEN_FAST")
            }
            label={t("word_screen_fast")}
            isDisabled={!isActive}
          />
        </Grid>

        {/* Stimulus Intensity */}
        <Grid item display="flex" flexDirection="column" xs gap="8px">
          <TitleWithTooltip
            title={t("report_stimulus_intensity")}
            tooltip={t("stimulus_intensity_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <TextField
              type="number"
              id="stimulus intensity input"
              disabled={!isActive}
              value={decibelValue}
              onChange={(event) => {
                const newDecibalValue = Number(event?.target?.value);
                setDecibelValue(newDecibalValue);
              }}
              onBlur={(event) => {
                const newDecibalValue = Number(event?.target?.value);
                if (newDecibalValue > MAX_DECIBEL_VALUE) {
                  setDecibelValue(MAX_DECIBEL_VALUE);
                  updateExamCardProperty("decibelValue", MAX_DECIBEL_VALUE);
                } else if (newDecibalValue < MIN_DECIBEL_VALUE) {
                  setDecibelValue(MIN_DECIBEL_VALUE);
                  updateExamCardProperty("decibelValue", MIN_DECIBEL_VALUE);
                } else {
                  setDecibelValue(newDecibalValue);
                  updateExamCardProperty("decibelValue", newDecibalValue);
                }
              }}
              onKeyPress={(event) => {
                const isNotNumber =
                  event?.key === "-" ||
                  event?.key === "+" ||
                  event?.key === ".";
                if (isNotNumber) return;

                const newDecibalValue = Number(event?.target?.value);
                setDecibelValue(newDecibalValue);
                updateExamCardProperty("decibelValue", newDecibalValue);
              }}
              fullWidth
              sx={{ ...CustomTextFieldSolid() }}
              InputProps={{
                inputProps: { min: MIN_DECIBEL_VALUE, max: MAX_DECIBEL_VALUE },
                sx: {
                  ...CustomTextFieldOutlinedInputProps,
                },
              }}
            />
          </Grid>
        </Grid>

        {/* grid */}
        <Grid container item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_grid")}
            tooltip={t("grid_size_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid item display="flex" gap="8px" height="32px">
            {vfScreenGrids.map((vfGrid, index) => (
              <Grid item xs key={index}>
                <ExamCardButton
                  isSelected={vfGrid.type === visualGrid}
                  updateValue={() =>
                    updateExamCardProperty("visualGrid", vfGrid.type)
                  }
                  label={vfGrid.label}
                  isDisabled={!isActive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* stimulus */}
        <Grid container item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("report_stimulus_size")}
            tooltip={t("stimulus_size_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid item display="flex" gap="8px">
            {vfScreenStimulusSizes.map((vfStimulusSize, index) => (
              <Grid item xs key={index}>
                <ExamCardButton
                  isSelected={vfStimulusSize.type === stimulusSizeType}
                  updateValue={() =>
                    updateExamCardProperty(
                      "stimulusSizeType",
                      vfStimulusSize.type
                    )
                  }
                  label={vfStimulusSize.label}
                  isDisabled={!isActive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* stimulus color */}
        <Grid container item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("report_stimulus_color")}
            tooltip={t("stimulus_color_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid item display="flex" gap="8px">
            {vfScreenStimulusColors.map((vfStimulusColor, index) => (
              <Grid item xs key={index}>
                <ExamCardButton
                  isSelected={vfStimulusColor.type === stimulusColor}
                  updateValue={() =>
                    updateExamCardProperty(
                      "stimulusColor",
                      vfStimulusColor.type
                    )
                  }
                  label={vfStimulusColor.label}
                  isDisabled={!isActive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Fixation */}
        <Grid container item display="flex" flexDirection="column" xs gap="8px">
          <TitleWithTooltip
            title={t("fixation")}
            tooltip={t("fixation_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={
                  FixationPointShapeOptions.Sphere ===
                  fixationType.fixationPointShapeType
                }
                updateValue={() =>
                  updateExamCardProperty("fixationType", {
                    ...examCardInfo.fixationType,
                    fixationPointShapeType: FixationPointShapeOptions.Sphere,
                  })
                }
                label={t("exams_modal_sphere")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={
                  FixationPointShapeOptions.Diamond ===
                  fixationType.fixationPointShapeType
                }
                updateValue={() =>
                  updateExamCardProperty("fixationType", {
                    ...examCardInfo.fixationType,
                    fixationPointShapeType: FixationPointShapeOptions.Diamond,
                  })
                }
                label={t("diamond")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>

          {/* Fixation loss */}
          <Grid item display="flex" flexDirection="column" xs gap="8px">
            <TitleWithTooltip
              title={t("fixation_loss_method")}
              tooltip={t("fixation_loss_method_tooltip")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" gap="8px" height="32px">
              <Grid item xs>
                <ExamCardButton
                  isSelected={
                    FixationLossMethodOptions.EyeTracking ===
                    fixationType.fixationLossMethod
                  }
                  updateValue={() =>
                    updateExamCardProperty("fixationType", {
                      ...examCardInfo.fixationType,
                      fixationLossMethod: FixationLossMethodOptions.EyeTracking,
                    })
                  }
                  label={t("eye_tracking")}
                  isDisabled={!isActive}
                />
              </Grid>
              <Grid item xs>
                <ExamCardButton
                  isSelected={
                    FixationLossMethodOptions.BlindSpot ===
                    fixationType.fixationLossMethod
                  }
                  updateValue={() =>
                    updateExamCardProperty("fixationType", {
                      ...examCardInfo.fixationType,
                      fixationLossMethod: FixationLossMethodOptions.BlindSpot,
                    })
                  }
                  label={t("word_blind_spot")}
                  isDisabled={!isActive}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <WhichEyeSection
          isActive={isActive}
          isRightFirst={sectionOrder.right === 0}
          eyeCheckboxes={eyeCheckboxes}
          onChangeSectionOrder={onChangeSectionOrder}
          onChangeEye={onChangeEye}
        />

        <Divider color="#5B6D7A" />

        <Grid display="flex" flexDirection="column">
          <ExamCardToggles
            isActive={isActive}
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={defaultVFScreening.optionalToggles}
          />
        </Grid>

        <Grid item textAlign="center" visibility="hidden">
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("new_test")}
          </Typography>
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("try_it_out")}
          </Typography>
        </Grid>
      </Grid>
    </ExamCardContainer>
  );
};

export default VisualFieldScreeningCard;
